import { cn } from '@/lib/utils';
import { t, getLocale } from '@/i18n';
import { OmyaLogo } from '@/components/icons/OmyaLogo';
import { HelpTip } from '@/components/ui/help-tip';

const fmt = (n) => (n == null ? '—' : n.toLocaleString(getLocale(), { maximumFractionDigits: 0 }));
export const monthLabel = (month) => new Date(`${month}-15`).toLocaleDateString(getLocale(), { month: 'long', year: 'numeric' });

// Same measures as the carrier scorecard (#22).
const METRICS = [
  { key: 'loadings', unit: '' },
  { key: 'announcedPct', unit: ' %' },
  { key: 'noShowPct', unit: ' %', lowerIsBetter: true, alarm: 15 },
  { key: 'outOfHours', unit: '', lowerIsBetter: true },
  { key: 'replyMedian', unit: ' min', lowerIsBetter: true },
  { key: 'onTimePct', unit: ' %' },
];

// Short findings for the meeting, derived from the numbers.
function findings({ now, prev }) {
  const out = [];
  if (now.noShowPct >= 15) out.push(t('rating.finding.noShowAlarm'));
  if (prev && Math.round(now.noShowPct) < Math.round(prev.noShowPct)) out.push(t('rating.finding.noShowBetter'));
  if (prev && Math.round(now.noShowPct) > Math.round(prev.noShowPct)) out.push(t('rating.finding.noShowWorse'));
  if (now.replyMedian != null && now.replyMedian > 30) out.push(t('rating.finding.slowReply'));
  if (now.onTimePct >= 90) out.push(t('rating.finding.onTime'));
  return out;
}

const Change = ({ now, prev, lowerIsBetter }) => {
  if (now == null || prev == null || Math.round(now) === Math.round(prev)) return <span className="text-muted-foreground">=</span>;
  const better = lowerIsBetter ? now < prev : now > prev;
  const diff = Math.round(now) - Math.round(prev);
  return <span className={better ? 'text-emerald-700' : 'text-red-700'}>{diff > 0 ? '+' : ''}{diff}</span>;
};

// The printable rating of one carrier for one month.
export const CarrierRatingSheet = ({ carrier, rating }) => (
  <article className="bg-card rounded-lg border p-6 sm:p-8 space-y-6 print:border-0 print:p-0">
    <header className="flex flex-wrap items-start gap-4 border-b pb-4">
      <div className="space-y-1 min-w-0">
        <div className="text-xs uppercase tracking-wider text-muted-foreground flex items-center gap-1">{t('rating.title')} <HelpTip id="rating" className="normal-case print:hidden" /></div>
        <h1 className="text-xl font-bold">{carrier.name}</h1>
        <div className="text-sm text-muted-foreground">
          {carrier.country} · {carrier.fleet} {t('stats.carrier.trucks')} · {carrier.dispatcher.name}, {carrier.dispatcher.phone}
        </div>
      </div>
      <div className="ml-auto text-right space-y-1">
        <OmyaLogo className="h-6 ml-auto" />
        <div className="text-sm font-semibold capitalize">{monthLabel(rating.month)}</div>
        <div className="text-xs text-muted-foreground">
          {t('rating.workDays').replace('{days}', rating.days)} · {t('rating.pilotShare').replace('{pct}', rating.pilotPct)}
        </div>
      </div>
    </header>

    <table className="w-full text-sm">
      <thead className="text-xs text-muted-foreground">
        <tr className="border-b">
          <th className="text-left font-medium py-2" />
          <th className="text-right font-medium py-2 px-2">{t('rating.thisMonth')}</th>
          <th className="text-right font-medium py-2 px-2">
            {rating.prevMonth ? <span className="capitalize">{monthLabel(rating.prevMonth)}</span> : t('rating.prevMonth')}
          </th>
          <th className="text-right font-medium py-2 pl-2">{t('rating.change')}</th>
        </tr>
      </thead>
      <tbody>
        {METRICS.map(m => {
          const now = rating.now[m.key];
          const prev = rating.prev?.[m.key];
          return (
            <tr key={m.key} className="border-b last:border-0">
              <td className="py-2">{t(`stats.carrier.${m.key}`)}</td>
              <td className={cn('py-2 px-2 text-right font-semibold tabular-nums', m.alarm && now >= m.alarm && 'text-red-700')}>
                {fmt(now)}{now != null && m.unit}
              </td>
              <td className="py-2 px-2 text-right tabular-nums text-muted-foreground">{fmt(prev)}{prev != null && m.unit}</td>
              <td className="py-2 pl-2 text-right tabular-nums"><Change now={now} prev={prev} lowerIsBetter={m.lowerIsBetter} /></td>
            </tr>
          );
        })}
      </tbody>
    </table>

    <section className="space-y-2">
      <h2 className="text-sm font-semibold">{t('rating.findings')}</h2>
      <ul className="list-disc pl-5 text-sm space-y-1">
        {findings(rating).map(f => <li key={f}>{f}</li>)}
      </ul>
    </section>

    <section className="space-y-2">
      <h2 className="text-sm font-semibold">{t('rating.notes')}</h2>
      {[0, 1, 2, 3].map(i => <div key={i} className="h-7 border-b border-dashed" />)}
    </section>

    <footer className="text-[11px] text-muted-foreground border-t pt-2">{t('rating.footer')}</footer>
  </article>
);
